import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import i18n from 'i18next';

type Props = {
  children: ReactNode;
};

export type Error = {
  message?: string | ReactNode;
  messageTemplate?: string | ReactNode;
  status?: number;
  response?: {
    data: {
      message?: string | ReactNode;
      messageTemplate?: string | ReactNode;
      status?: number;
    };
  };
};

type ErrorContextType = {
  error: Error | undefined;
  showError: (error?: Error) => void;
  removeError: () => void;
};

const ErrorContext = createContext<ErrorContextType>({} as ErrorContextType);

const useError = (): ErrorContextType => useContext(ErrorContext);
const ErrorProvider = ({ children }: Props): JSX.Element => {
  const [error, setError] = useState<Error | undefined>();

  const showError = useCallback((error: Error = {}) => {
    const defaultError: Error = {
      message: i18n.t('error:generic'),
    };

    setError({
      ...defaultError,
      ...error,
    });
  }, []);

  const removeError = useCallback(() => {
    setError(undefined);
  }, []);

  const errorContextValue = useMemo(
    () => ({
      error,
      showError,
      removeError,
    }),
    [error, showError, removeError],
  );
  return (
    <ErrorContext.Provider value={errorContextValue}>
      {children}
    </ErrorContext.Provider>
  );
};

export { useError as default, ErrorProvider };
